import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getMiscReport } from "./miscReportService";
import {
  FaUndo,
  FaFileInvoice,
  FaFileAlt,
  FaShoppingCart,
  FaChartBar,
  FaDownload,
  FaPrint,
  FaArrowLeft,
  FaExclamationTriangle,
  FaFolderOpen
} from "react-icons/fa";
import "./MiscReport.css";
import { usePermissions } from "../../context/PermissionContext";
import AccessDenied from "../ui/AccessDenied";

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const fmtDate = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const StatusBadge = ({ status }) => {
  const s = (status || "").toLowerCase();
  return <span className={`misc-status-badge ${s}`}>{status || "—"}</span>;
};

const TABS = [
  { key: "refunds",   label: "Refunds",        icon: <FaUndo /> },
  { key: "credit",    label: "Credit Notes",   icon: <FaFileInvoice /> },
  { key: "debit",     label: "Debit Notes",    icon: <FaFileAlt /> },
  { key: "purchases", label: "Purchase Bills", icon: <FaShoppingCart /> },
];

const MiscReportPage = () => {
  const navigate = useNavigate();
  const { canView, canExport } = usePermissions();

  if (!canView("misc")) {
    return <AccessDenied moduleName="Miscellaneous Report" action="view" />;
  }

  // ── Filters ─────────────────────────────────────────────
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // ── Data ────────────────────────────────────────────────
  const [summary, setSummary] = useState(null);
  const [refunds, setRefunds] = useState([]);
  const [creditNotes, setCreditNotes] = useState([]);
  const [debitNotes, setDebitNotes] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ── Active tab ───────────────────────────────────────────
  const [activeTab, setActiveTab] = useState("refunds");

  const hasFetched = useRef(false);

  // ── Fetch ────────────────────────────────────────────────
  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters = {
        start_date: startDate || undefined,
        end_date: endDate || undefined,
      };
      const data = await getMiscReport(filters);
      setSummary(data.summary || null);
      setRefunds(data.refunds || []);
      setCreditNotes(data.credit_notes || []);
      setDebitNotes(data.debit_notes || []);
      setPurchases(data.purchase_bills || []);
    } catch (err) {
      console.error("Misc Report fetch error:", err);
      setError("Failed to load the Miscellaneous Report. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchReport();
    }
  }, [fetchReport]);

  // ── Reset ────────────────────────────────────────────────
  const handleReset = () => {
    setStartDate("");
    setEndDate("");
    hasFetched.current = false;
    setTimeout(() => {
      hasFetched.current = true;
      fetchReport();
    }, 0);
  };

  // ── Export CSV ───────────────────────────────────────────
  const handleExportCSV = () => {
    let rows = [];
    if (activeTab === "refunds") {
      rows = [
        ["ID", "Sale ID", "Refund Date", "Total Refund", "Cancellation Fee", "Status", "Notes"],
        ...refunds.map((r) => [
          r.id, r.sale_id, r.refund_date, r.total_refund,
          r.cancellation_fee, r.status, r.notes || "",
        ]),
      ];
    } else if (activeTab === "credit") {
      rows = [
        ["Note #", "Date", "Customer", "Amount", "Tax", "Total", "Status", "Reason"],
        ...creditNotes.map((c) => [
          c.note_number, c.date, c.customer_name, c.amount,
          c.tax_amount, c.total_amount, c.status, c.reason || "",
        ]),
      ];
    } else if (activeTab === "debit") {
      rows = [
        ["Note #", "Date", "Supplier", "Amount", "Tax", "Total", "Status", "Reason"],
        ...debitNotes.map((d) => [
          d.note_number, d.date, d.supplier_name, d.amount,
          d.tax_amount, d.total_amount, d.status, d.reason || "",
        ]),
      ];
    } else if (activeTab === "purchases") {
      rows = [
        ["Bill #", "Date", "Supplier", "Amount", "Tax", "Total", "Status"],
        ...purchases.map((p) => [
          p.bill_number, p.bill_date, p.supplier_name,
          p.amount, p.tax_amount, p.total_amount, p.status,
        ]),
      ];
    }

    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `misc_report_${activeTab}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Tab count map ─────────────────────────────────────────
  const tabCounts = {
    refunds:   refunds.length,
    credit:    creditNotes.length,
    debit:     debitNotes.length,
    purchases: purchases.length,
  };

  return (
    <div className="misc-report-page">
      {/* ── Header ─────────────────────────────────────────── */}
      <div className="misc-report-header">
        <div className="misc-report-title-group">
          <h2><FaFolderOpen style={{ color: "#3b82f6" }} /> Miscellaneous Report</h2>
          <p>Refunds · Credit Notes · Debit Notes · Purchase Bills</p>
        </div>
        <div className="misc-header-actions">
          <button className="misc-btn misc-btn-export" onClick={handleExportCSV}>
            <FaDownload /> Export CSV
          </button>
          <button className="misc-btn misc-btn-print" onClick={() => window.print()}>
            <FaPrint /> Print
          </button>
          <button
            className="misc-btn misc-btn-back"
            onClick={() => navigate("/dashboard")}
          >
            <FaArrowLeft /> Dashboard
          </button>
        </div>
      </div>

      {/* ── Summary Cards ───────────────────────────────────── */}
      {summary && (
        <div className="misc-summary-grid">
          <div className="misc-stat-card refunds">
            <div className="misc-stat-icon" style={{ color: "#f59e0b" }}><FaUndo /></div>
            <div className="misc-stat-label">Total Refunds</div>
            <div className="misc-stat-value">AED {fmt(summary.total_refunds)}</div>
            <div className="misc-stat-sub">{summary.refund_count} transactions</div>
          </div>
          <div className="misc-stat-card credit">
            <div className="misc-stat-icon" style={{ color: "#10b981" }}><FaFileInvoice /></div>
            <div className="misc-stat-label">Credit Notes</div>
            <div className="misc-stat-value">AED {fmt(summary.total_credit_notes)}</div>
            <div className="misc-stat-sub">{summary.credit_note_count} notes</div>
          </div>
          <div className="misc-stat-card debit">
            <div className="misc-stat-icon" style={{ color: "#ef4444" }}><FaFileAlt /></div>
            <div className="misc-stat-label">Debit Notes</div>
            <div className="misc-stat-value">AED {fmt(summary.total_debit_notes)}</div>
            <div className="misc-stat-sub">{summary.debit_note_count} notes</div>
          </div>
          <div className="misc-stat-card purchases">
            <div className="misc-stat-icon" style={{ color: "#6366f1" }}><FaShoppingCart /></div>
            <div className="misc-stat-label">Purchase Bills</div>
            <div className="misc-stat-value">AED {fmt(summary.total_purchase_bills)}</div>
            <div className="misc-stat-sub">{summary.purchase_bill_count} bills</div>
          </div>
          <div className="misc-stat-card net">
            <div className="misc-stat-icon" style={{ color: "#0ea5e9" }}><FaChartBar /></div>
            <div className="misc-stat-label">Net Adjustments</div>
            <div className="misc-stat-value">AED {fmt(summary.net_adjustments)}</div>
            <div className="misc-stat-sub">Credit − Debit − Refunds</div>
          </div>
        </div>
      )}

      {/* ── Filters ─────────────────────────────────────────── */}
      <div className="misc-filters-bar">
        <div className="misc-filter-group">
          <label>Date From</label>
          <input
            id="misc-start-date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div className="misc-filter-group">
          <label>Date To</label>
          <input
            id="misc-end-date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <button id="misc-apply-btn" className="misc-btn misc-btn-filter" onClick={fetchReport}>
          Apply Filters
        </button>
        <button id="misc-reset-btn" className="misc-btn misc-btn-reset" onClick={handleReset}>
          Reset
        </button>
      </div>

      {/* ── Loading / Error ──────────────────────────────────── */}
      {loading && (
        <div className="misc-loading">
          <div className="misc-spinner" />
          Loading miscellaneous report…
        </div>
      )}

      {error && <div className="misc-error"><FaExclamationTriangle /> {error}</div>}

      {!loading && !error && (
        <>
          {/* ── Tabs ───────────────────────────────────────────── */}
          <div className="misc-tabs">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                className={`misc-tab${activeTab === tab.key ? " active" : ""}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.icon} {tab.label}
                <span className="misc-tab-count">{tabCounts[tab.key]}</span>
              </button>
            ))}
          </div>

          {/* ── Tab Content ─────────────────────────────────────── */}

          {/* Refunds */}
          {activeTab === "refunds" && (
            <div className="misc-table-card">
              <div className="misc-table-header">
                <strong>Refunds</strong>
                <span>{refunds.length} record{refunds.length !== 1 ? "s" : ""}</span>
              </div>
              {refunds.length === 0 ? (
                <div className="misc-empty">
                  <div className="misc-empty-icon" style={{ color: "#f59e0b" }}><FaUndo /></div>
                  No refunds found for the selected period.
                </div>
              ) : (
                <div className="misc-table-wrapper">
                  <table className="misc-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Sale ID</th>
                        <th>Customer</th>
                        <th>Refund Date</th>
                        <th className="num">Total Refund (AED)</th>
                        <th className="num">Cancellation Fee (AED)</th>
                        <th>Status</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {refunds.map((r, idx) => (
                        <tr key={r.id}>
                          <td style={{ color: "#94a3b8", fontSize: "0.78rem" }}>{idx + 1}</td>
                          <td style={{ fontWeight: 500 }}>#{r.sale_id}</td>
                          <td>{r.customer_name || "—"}</td>
                          <td>{fmtDate(r.refund_date)}</td>
                          <td className="num">
                            <span className="misc-amount-negative">{fmt(r.total_refund)}</span>
                          </td>
                          <td className="num">{fmt(r.cancellation_fee)}</td>
                          <td><StatusBadge status={r.status} /></td>
                          <td style={{ fontSize: "0.82rem", color: "#64748b" }}>
                            {r.notes || "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={4}><strong>Total</strong></td>
                        <td className="num">
                          <span className="misc-amount-negative">
                            {fmt(refunds.reduce((a, r) => a + Number(r.total_refund || 0), 0))}
                          </span>
                        </td>
                        <td className="num">
                          {fmt(refunds.reduce((a, r) => a + Number(r.cancellation_fee || 0), 0))}
                        </td>
                        <td colSpan={2}></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Credit Notes */}
          {activeTab === "credit" && (
            <div className="misc-table-card">
              <div className="misc-table-header">
                <strong>Credit Notes</strong>
                <span>{creditNotes.length} record{creditNotes.length !== 1 ? "s" : ""}</span>
              </div>
              {creditNotes.length === 0 ? (
                <div className="misc-empty">
                  <div className="misc-empty-icon" style={{ color: "#10b981" }}><FaFileInvoice /></div>
                  No credit notes found for the selected period.
                </div>
              ) : (
                <div className="misc-table-wrapper">
                  <table className="misc-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Note #</th>
                        <th>Date</th>
                        <th>Customer</th>
                        <th className="num">Amount (AED)</th>
                        <th className="num">Tax (AED)</th>
                        <th className="num">Total (AED)</th>
                        <th>Status</th>
                        <th>Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {creditNotes.map((c, idx) => (
                        <tr key={c.id}>
                          <td style={{ color: "#94a3b8", fontSize: "0.78rem" }}>{idx + 1}</td>
                          <td style={{ fontWeight: 600, color: "#6366f1" }}>{c.note_number}</td>
                          <td>{fmtDate(c.date)}</td>
                          <td>{c.customer_name || "—"}</td>
                          <td className="num">{fmt(c.amount)}</td>
                          <td className="num">{fmt(c.tax_amount)}</td>
                          <td className="num">
                            <span className="misc-amount-positive">{fmt(c.total_amount)}</span>
                          </td>
                          <td><StatusBadge status={c.status} /></td>
                          <td style={{ fontSize: "0.82rem", color: "#64748b" }}>
                            {c.reason || "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={4}><strong>Total</strong></td>
                        <td className="num">
                          {fmt(creditNotes.reduce((a, c) => a + Number(c.amount || 0), 0))}
                        </td>
                        <td className="num">
                          {fmt(creditNotes.reduce((a, c) => a + Number(c.tax_amount || 0), 0))}
                        </td>
                        <td className="num">
                          <span className="misc-amount-positive">
                            {fmt(creditNotes.reduce((a, c) => a + Number(c.total_amount || 0), 0))}
                          </span>
                        </td>
                        <td colSpan={2}></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Debit Notes */}
          {activeTab === "debit" && (
            <div className="misc-table-card">
              <div className="misc-table-header">
                <strong>Debit Notes</strong>
                <span>{debitNotes.length} record{debitNotes.length !== 1 ? "s" : ""}</span>
              </div>
              {debitNotes.length === 0 ? (
                <div className="misc-empty">
                  <div className="misc-empty-icon" style={{ color: "#ef4444" }}><FaFileAlt /></div>
                  No debit notes found for the selected period.
                </div>
              ) : (
                <div className="misc-table-wrapper">
                  <table className="misc-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Note #</th>
                        <th>Date</th>
                        <th>Supplier</th>
                        <th className="num">Amount (AED)</th>
                        <th className="num">Tax (AED)</th>
                        <th className="num">Total (AED)</th>
                        <th>Status</th>
                        <th>Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {debitNotes.map((d, idx) => (
                        <tr key={d.id}>
                          <td style={{ color: "#94a3b8", fontSize: "0.78rem" }}>{idx + 1}</td>
                          <td style={{ fontWeight: 600, color: "#ef4444" }}>{d.note_number}</td>
                          <td>{fmtDate(d.date)}</td>
                          <td>{d.supplier_name || "—"}</td>
                          <td className="num">{fmt(d.amount)}</td>
                          <td className="num">{fmt(d.tax_amount)}</td>
                          <td className="num">
                            <span className="misc-amount-negative">{fmt(d.total_amount)}</span>
                          </td>
                          <td><StatusBadge status={d.status} /></td>
                          <td style={{ fontSize: "0.82rem", color: "#64748b" }}>
                            {d.reason || "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={4}><strong>Total</strong></td>
                        <td className="num">
                          {fmt(debitNotes.reduce((a, d) => a + Number(d.amount || 0), 0))}
                        </td>
                        <td className="num">
                          {fmt(debitNotes.reduce((a, d) => a + Number(d.tax_amount || 0), 0))}
                        </td>
                        <td className="num">
                          <span className="misc-amount-negative">
                            {fmt(debitNotes.reduce((a, d) => a + Number(d.total_amount || 0), 0))}
                          </span>
                        </td>
                        <td colSpan={2}></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Purchase Bills */}
          {activeTab === "purchases" && (
            <div className="misc-table-card">
              <div className="misc-table-header">
                <strong>Purchase Bills</strong>
                <span>{purchases.length} record{purchases.length !== 1 ? "s" : ""}</span>
              </div>
              {purchases.length === 0 ? (
                <div className="misc-empty">
                  <div className="misc-empty-icon" style={{ color: "#6366f1" }}><FaShoppingCart /></div>
                  No purchase bills found for the selected period.
                </div>
              ) : (
                <div className="misc-table-wrapper">
                  <table className="misc-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Bill #</th>
                        <th>Date</th>
                        <th>Supplier</th>
                        <th>Due Date</th>
                        <th className="num">Amount (AED)</th>
                        <th className="num">Tax (AED)</th>
                        <th className="num">Total (AED)</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {purchases.map((p, idx) => (
                        <tr key={p.id}>
                          <td style={{ color: "#94a3b8", fontSize: "0.78rem" }}>{idx + 1}</td>
                          <td style={{ fontWeight: 600, color: "#6366f1" }}>
                            {p.bill_number || "—"}
                          </td>
                          <td>{fmtDate(p.bill_date)}</td>
                          <td>{p.supplier_name || "—"}</td>
                          <td>{fmtDate(p.due_date)}</td>
                          <td className="num">{fmt(p.amount)}</td>
                          <td className="num">{fmt(p.tax_amount)}</td>
                          <td className="num">
                            <span className="misc-amount-neutral">{fmt(p.total_amount)}</span>
                          </td>
                          <td><StatusBadge status={p.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={5}><strong>Total</strong></td>
                        <td className="num">
                          {fmt(purchases.reduce((a, p) => a + Number(p.amount || 0), 0))}
                        </td>
                        <td className="num">
                          {fmt(purchases.reduce((a, p) => a + Number(p.tax_amount || 0), 0))}
                        </td>
                        <td className="num">
                          <span className="misc-amount-neutral">
                            {fmt(purchases.reduce((a, p) => a + Number(p.total_amount || 0), 0))}
                          </span>
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MiscReportPage;
